import { isEmpty } from 'lodash-es';
import moment from 'moment';

import { DashboardCardValueType } from '~/src/dash/components/cards/common';
import { formatNumberForDisplay, ValueType } from '~/src/dash/components/tables/common';

export type ChartType = 'bar' | 'line';

export interface IDrillBarChartDataItem {
  id: string;
  name: string;
  primaryValue: number;
  valueType: ValueType;
  comparisonValue?: number;
  percentChange?: number;
  color?: string;
}

export const CHART_COLORS = [
  ['#5D66FA', '#B6BAFF'], // blue
  ['#FF4E29', '#FFBAAB'], // red
  ['#00B7A9', '#A3EAE4'], // bluegreen
  ['#F3C628', '#FFE380'], // goldenrod
  ['#84BCEF', '#C9E4FC'], // bluegray
  ['#BC83FF', '#DEC2FF'], // purple
];

export type YScale =
  | 'small' // reserve for ~ < 100
  | 'base' // ex. revenue
  | 'large'; // ex. impressions

export interface IDashboardChartMetric {
  key: string;
  label: string;
  valueType: DashboardCardValueType;
  chartType: ChartType;
  chartColorIndex: number;
  yScale?: YScale;
}

export interface IDashboardSubChartMetric {
  key: string;
  label: string;
  responseKey: string;
  valueType: ValueType;
  color: string;
  isDisabled: (props: any) => boolean;
  yScale?: YScale;
}

export const formatTooltipDate = (date: string | moment.Moment, grouping?: number): string => {
  if (!date) {
    return '';
  }
  switch (grouping) {
    case 0:
      return moment(date).utc().format('HH:mm (ddd MM/DD/YY)');
    default:
      return moment(date).utc().format('ddd MM/DD/YY');
  }
};

export const formatTooltipValue = (
  value: number | null,
  valueType: ValueType,
  currencySymbol: string,
) => {
  if (value === null) {
    return 'N/A';
  }
  switch (valueType) {
    case 'whole_number':
      return formatNumberForDisplay(Math.ceil(value), 0);
    case 'integer':
      return formatNumberForDisplay(value);
    case 'percent':
      return `${value.toFixed(2)}%`;
    case 'currency':
      return `${currencySymbol} ${formatNumberForDisplay(value, 2)}`;
    case 'currency_rate':
      return `${currencySymbol} ${value.toFixed(value > 1 ? 2 : 3)}`;
    case 'latency':
      return `${formatNumberForDisplay(Math.ceil(value), 0)} ms`;
    default:
      return 'N/A';
  }
};

export const mergeCompareData = (
  main: Array<Record<string, any>>,
  compare: Array<Record<string, any>> = [],
  isCompareEnabled?: boolean,
  grouping?: number,
) => {
  const merged: Array<Record<string, any>> = [];
  if (isEmpty(main)) {
    return merged;
  }
  if (!isCompareEnabled || isEmpty(compare)) {
    main.forEach((item) => {
      merged.push({
        main: item,
        compare: {},
      });
    });
  } else {
    const maxLength = Math.max(main.length, (compare || []).length);
    // below handles spring forward when grouped by hour
    // TODO: fall back hours must be handled on the backend
    if (grouping === 0 && main.length !== compare.length) {
      let mainIndex = 0;
      let compIndex = 0;
      for (let i = 0; i < maxLength; i++) {
        const mainHour = main[mainIndex]?.Date.slice(11, 13) || undefined;
        const compHour = compare[compIndex]?.Date.slice(11, 13) || undefined;
        if (mainHour === compHour) {
          merged.push({
            main: main[mainIndex] || {},
            compare: compare ? compare[compIndex] : {},
          });
          mainIndex++;
          compIndex++;
        } else {
          if (mainHour && compHour && Number(compHour) > Number(mainHour)) {
            merged.push({
              main: main[mainIndex] || {},
              compare: {},
            });
            mainIndex++;
          } else if (mainHour && compHour && Number(mainHour) > Number(compHour)) {
            merged.push({
              main: {},
              compare: compare ? compare[compIndex] : {},
            });
            compIndex++;
          } else if (mainHour && !compHour) {
            merged.push({
              main: main[mainIndex] || {},
              compare: {},
            });
            mainIndex++;
            compIndex++;
          } else if (!mainHour && compHour) {
            merged.push({
              main: {},
              compare: compare ? compare[compIndex] : {},
            });
            mainIndex++;
            compIndex++;
          } else {
            merged.push({
              main: main[mainIndex] || {},
              compare: compare ? compare[compIndex] : {},
            });
            mainIndex++;
            compIndex++;
          }
        }
      }
    } else {
      for (let i = 0; i < maxLength; i++) {
        merged.push({
          main: main[i] || {},
          compare: compare ? compare[i] : {},
        });
      }
    }
  }
  return merged;
};

interface IScale {
  hide: boolean;
  orientation: 'left' | 'right';
}

interface IScaleMetrics {
  yScale?: YScale;
}

// helps determine which yAxes are visible
export const getScalesInfo = (metrics: IScaleMetrics[]) => {
  const visibleScales: YScale[] = [];
  let isBaseVisible = false;
  for (const metric of metrics) {
    const yScale = metric.yScale || 'base';
    // if base scale is active, it must default to visible on the left
    if (yScale === 'base') isBaseVisible = true;
    if (!visibleScales.includes(yScale)) {
      if (yScale === 'base') {
        visibleScales.unshift(yScale);
      } else {
        visibleScales.push(yScale);
      }
    }
  }
  const scales: Record<string, IScale> = {
    base: { hide: true, orientation: 'left' },
    small: { hide: true, orientation: 'left' },
    large: { hide: true, orientation: 'left' },
  };
  // we only show 2 axes at a time
  const limit = Math.min(2, visibleScales.length);
  let numVisibleAxes = 0;
  for (let i = 0; i < limit; i++) {
    const scale = visibleScales[i];
    scales[scale].hide = false;
    if (scale !== 'base') {
      scales[scale].orientation = !isBaseVisible && i === 0 ? 'left' : 'right';
    }
    numVisibleAxes++;
  }
  return { scales, numVisibleAxes };
};
